import { Box, Button, Grid, Grid2, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import * as React from 'react';
import { useApi } from '../Api/useApi';
import { CreateFileGetUploadSasRequest, FileDto, FilesRycApiClient } from '../Api/ApiServer';
import { useParams } from 'react-router';
import { ArrowLeft, ArrowRight, Download, Upload } from '@mui/icons-material';
import { RycIsProjectModule } from '../Enums/RycIsProjectModule';
import { useProject } from '../Contexts/Hooks/useProject';
import { handleBlobFileUpload } from '../Uploader/HandleBlobFileUpload';
import { useDropzone } from 'react-dropzone';
import SearchBox from '../Shared/SearchBox';
import PdfViewer from './FileViews/PdfViewer';
import OfficeViewer from './FileViews/OfficeViewer';
import ImageViewer from './FileViews/ImageViewer';
import FileListItem from './FileListItem';
import ShareFileDialog from './ShareFileDialog';



export interface IProjectFilesProps {
}

export default function ProjectFiles(props: IProjectFilesProps) {
    const { authorizedApi } = useApi(FilesRycApiClient);
    const [files, setFiles] = React.useState<FileDto[]>([]);
    const [visibleFiles, setVisibleFiles] = React.useState<FileDto[]>([]);
    const [selectedFile, setSelectedFile] = React.useState<FileDto | null>(null);
    const [fileDownloadUri, setFileDownloadUri] = React.useState<string | null>(null);
    const [fileShareUri, setFileShareUri] = React.useState<string | null>(null);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const params = useParams();
    const loadFiles = React.useCallback(async () => {
        const files = await authorizedApi?.filesForProject(Number(params['id']));
        setFiles(files?.files ?? []);
    }, [authorizedApi, params]);
    const loadDownloadUri = React.useCallback(async () => {
        if (selectedFile === null) { setFileDownloadUri(null); return; }
        const file = await authorizedApi?.downloadSasToken(selectedFile.id!);

        setFileDownloadUri(file?.sasToken ?? null);
    }, [selectedFile, authorizedApi]);
    const deleteFile = React.useCallback(async (file: FileDto) => {
        if (file.id)
            authorizedApi?.deleteFile(file.id);
    }, [authorizedApi]);
    React.useEffect(() => {
        loadFiles();
    }, [loadFiles]);
    React.useEffect(() => {
        setVisibleFiles(files);
    }, [files]);
    React.useEffect(() => {
        loadDownloadUri();
    }, [loadDownloadUri]);
    const { setCurrentModule, setMenuButtons } = useProject();
    React.useEffect(() => {
        setCurrentModule(RycIsProjectModule.Files);
    }, [setCurrentModule]);
    React.useEffect(() => { setMenuButtons(<> </>); }, [setMenuButtons]);
    const upload = React.useCallback(async (file: File) => {
        const sasUri = await authorizedApi?.createFileGetUploadSasUri(new CreateFileGetUploadSasRequest({
            fileName: file.name,
            projectId: Number(params['id'])
        }));
        await handleBlobFileUpload(file, sasUri?.sasToken!);
    }, [params, authorizedApi]);
    const share = React.useCallback(async (file: FileDto) => {
        if (!file.id) return;
        const sasUri = await authorizedApi?.getShareLink(file.id);
        setFileShareUri(sasUri?.link ?? null);
    }, [authorizedApi]);


    const onDrop = React.useCallback(async (acceptedFiles: File[]) => {
        // Handle file upload here
        for (let file of acceptedFiles)
            // acceptedFiles.forEach(async (file) => {
            await upload(file);
        // });
        loadFiles();
    }, [upload, loadFiles]);

    const { getRootProps, isDragActive } = useDropzone({ onDrop });
    const boxRef = React.useRef<HTMLDivElement>(null);
    const [viewerWidth, setViewerWidth] = React.useState(660);
    // Add this effect to update width when component mounts or window resizes
    React.useEffect(() => {
        const updateWidth = () => {
            if (boxRef.current) {
                // Account for padding (16px on each side)
                const width = boxRef.current.clientWidth - 32;
                setViewerWidth(width);
            }
        };

        // Initial measurement
        updateWidth();

        // Update on resize
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);
    return (
        <Grid2 overflow={'auto'} textAlign={'left'}
            width={'100vw'} maxHeight={'calc(100vh - 100px)'} mb={4} p={2} spacing={3} container>
            <ShareFileDialog open={fileShareUri !== null} onClose={() => setFileShareUri(null)} shareUrl={fileShareUri ?? ''} />
            <Grid2 size={{ xs: 12 }}>
                <Stack direction='row' display='flex' spacing={2}>
                    <Tooltip title='Nahrát IFC'>
                        <Button size='small' variant='contained'
                            onClick={() => fileInputRef.current?.click()}
                        ><Upload /></Button>
                    </Tooltip>
                    {/* <SearchBox label='Hledat projekt' sxWrap={{ ml: 2 }} size='small' items={projects} onSearchChange={(_, projects) => { setVisibleProjects(projects) }} propsToSearch={['name', 'partnerName']} /> */}
                    <SearchBox<FileDto>
                        items={files}
                        label='Hledat dokumenty...'
                        size='small'
                        onSearchChange={(_, files) => { setVisibleFiles(files); }}
                        propsToSearch={['name', 'extension']}

                    />
                    <input
                        ref={fileInputRef}
                        type="file"

                        style={{
                            display: 'none'
                        }} onChange={async (e) => {
                            if (e.target.files) {
                                for (let i = 0; i < e.target.files.length; i++) { await upload(e.target.files[i]); }
                                loadFiles();
                            }
                        }} />



                </Stack>
            </Grid2>
            {/* {selectedFile !== null && selectedFile.extension === '.pdf' && fileDownloadUri && } */}
            <Grid2 overflow='auto' height={'calc(100vh - 240px)'} size={{ xs: 12, sm: 12, md: 5, lg: 5, xl: 5 }}
                {...getRootProps()}
                sx={{
                    minHeight: '200px',
                    border: isDragActive ? '2px dashed #2196f3' : 'none',
                    backgroundColor: isDragActive ? 'rgba(33, 150, 243, 0.1)' : 'transparent',
                    padding: '20px',
                    borderRadius: '4px',
                    transition: 'all .2s ease-in-out'
                }}>

                {/* <input {...getInputProps()} /> */}
                {visibleFiles.map((file, index) => <FileListItem
                    key={file.id}
                    onShare={share}
                    file={file}
                    onDelete={deleteFile}
                    fileDownloadUri={fileDownloadUri}
                    selectedFile={selectedFile}
                    setSelectedFile={setSelectedFile} />)}

                {isDragActive ? (
                    <Typography sx={{ textAlign: 'center', mt: 2 }}>Pusťte soubory pro nahrání ...</Typography>
                ) : (
                    <Typography sx={{ textAlign: 'center', mt: 2 }}></Typography>
                )}
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 7, lg: 7, xl: 7 }}>
                <Box ref={boxRef}>
                    {selectedFile === null && <Box>Vyberte soubor</Box>}
                    {selectedFile !== null && selectedFile.extension === '.pdf' && fileDownloadUri &&
                        <PdfViewer
                            currentWidth={viewerWidth}
                            fileDownloadUri={fileDownloadUri}
                        />}
                    {selectedFile !== null &&
                        (selectedFile.extension === '.png' ||
                            selectedFile.extension === '.jpg' ||
                            selectedFile.extension === '.jpeg' ||
                            selectedFile.extension === '.gif') &&
                        fileDownloadUri &&
                        <ImageViewer fileDownloadUri={fileDownloadUri} alt={selectedFile.name ?? 'obrázek'}></ImageViewer>
                    }
                    {selectedFile !== null &&
                        (selectedFile.extension === '.xlsx' ||
                            selectedFile.extension === '.xls' ||
                            selectedFile.extension === '.docx' ||
                            selectedFile.extension === '.doc') &&
                        fileDownloadUri &&
                        <OfficeViewer currentWidth={viewerWidth} fileDownloadUri={fileDownloadUri} />
                    }
                </Box>
            </Grid2>
        </Grid2>
    );
}
